// ─── User ────────────────────────────────────────────────────────────────────

export type UserRole = 'admin' | 'customer' | 'employee';

/** User verification lifecycle */
export type UserVerificationStatus =
  | 'pending_profile'    // Just registered, must fill billing & shipping
  | 'pending_verification' // Profile submitted, awaiting admin review
  | 'verified'           // Approved by admin
  | 'rejected';          // Rejected by admin — must fix profile

export interface BillingProfile {
  vatNumber: string;
  sdiNumber: string;
  shippingAddress: {
    street: string;
    city: string;
    province: string;
    postalCode: string;
    country: string;
  };
}

export interface User {
  id: string;
  email: string;
  passwordHash: string;
  salt: string;
  companyName: string;
  contactName: string;
  role: UserRole;
  verificationStatus: UserVerificationStatus;
  billingProfile?: BillingProfile;
  rejectionReason?: string; // set by admin when rejected
  mustChangePassword?: boolean; // set on enrollment, cleared after first password change
  permissions?: OAuthScope[];   // employee-only: subset of admin scopes
  createdAt: string; // ISO 8601
}

export type PublicUser = Omit<User, 'passwordHash' | 'salt'>;

// ─── Product ─────────────────────────────────────────────────────────────────

export interface ProductVariant {
  size: string;        // e.g. "330ml", "500ml", "1L"
  unit: string;        // e.g. "can", "bottle"
  pricePerUnit: number; // EUR cents
  minOrderQty: number;
  bulkPricing?: BulkTier[];
  imageUrl?: string;   // variant-specific image
}

export interface BulkTier {
  minQty: number;
  pricePerUnit: number; // EUR cents
}

export type ProductCategory =
  | 'soda'
  | 'energy'
  | 'brainstorm'
  | 'hydrate'
  | 'cold-brew';

export interface Product {
  id: string;
  name: string;
  description: string;
  category: ProductCategory;
  variants: ProductVariant[];
  active: boolean;
  imageUrl: string;
  createdAt: string;
  updatedAt: string;
}

// ─── Cart ────────────────────────────────────────────────────────────────────

export interface CartItem {
  productId: string;
  productName: string;
  variantIndex: number;
  quantity: number;
  pricePerUnit: number; // snapshot at time of add
}

export interface Cart {
  userId: string;
  items: CartItem[];
  updatedAt: string;
}

// ─── Order ───────────────────────────────────────────────────────────────────

export type OrderStatus =
  | 'verification_needed'  // placed by unverified user
  | 'pending'
  | 'confirmed'
  | 'packed'
  | 'shipped'
  | 'delivered'
  | 'problem';

export interface OrderItem {
  productId: string;
  productName: string;
  variantSize: string;
  variantUnit: string;
  quantity: number;
  pricePerUnit: number;
  subtotal: number;
}

export interface OrderStatusChange {
  status: OrderStatus;
  timestamp: string;
  note?: string;
}

export interface Order {
  id: string;
  userId: string;
  companyName: string;
  items: OrderItem[];
  status: OrderStatus;
  statusHistory: OrderStatusChange[];
  total: number; // EUR cents
  note?: string;
  createdAt: string;
  updatedAt: string;
}

// ─── Ticket ──────────────────────────────────────────────────────────────────

export type TicketStatus =
  | 'open'
  | 'in-progress-l1'
  | 'in-progress-l2'
  | 'resolved'
  | 'closed';

export type TicketPriority = 'normal' | 'urgent';

export type TicketQueue =
  | 'general'
  | 'billing'
  | 'shipping'
  | 'product-issue'
  | 'returns'
  | 'technical';

export const TICKET_QUEUE_LABELS: Record<TicketQueue, string> = {
  general: 'General Support',
  billing: 'Billing & Payments',
  shipping: 'Shipping & Delivery',
  'product-issue': 'Product Issues',
  returns: 'Returns & Refunds',
  technical: 'Technical Support',
};

export const TICKET_STATUS_LABELS: Record<TicketStatus, string> = {
  open: 'Open',
  'in-progress-l1': 'In Progress (L1)',
  'in-progress-l2': 'In Progress (L2)',
  resolved: 'Resolved',
  closed: 'Closed',
};

export interface TicketMessage {
  id: string;
  from: 'customer' | 'admin' | 'employee';
  senderId: string;
  senderName: string;
  text: string;
  internal: boolean; // only visible to admin/employee
  timestamp: string;
}

export interface TicketAssignment {
  assignedById: string;
  assignedByName: string;
  assignedToId: string;
  assignedToName: string;
  timestamp: string;
}

export interface Ticket {
  id: string;
  userId: string;
  companyName: string;
  orderId?: string;
  subject: string;
  queue: TicketQueue;
  status: TicketStatus;
  priority: TicketPriority;
  messages: TicketMessage[];
  assignment?: TicketAssignment;
  createdAt: string;
  updatedAt: string;
}

// ─── OAuth2 / Delegation ─────────────────────────────────────────────────────

export type OAuthScope =
  | 'store:read'
  | 'cart:read'
  | 'cart:write'
  | 'orders:read'
  | 'orders:write'
  | 'tickets:read'
  | 'tickets:write'
  | 'profile:read'
  | 'profile:write'
  | 'admin:products'
  | 'admin:orders'
  | 'admin:tickets'
  | 'admin:invites'
  | 'admin:users'
  | 'admin:enrollment';

export const ALL_OAUTH_SCOPES: OAuthScope[] = [
  'store:read',
  'cart:read',
  'cart:write',
  'orders:read',
  'orders:write',
  'tickets:read',
  'tickets:write',
  'profile:read',
  'profile:write',
  'admin:products',
  'admin:orders',
  'admin:tickets',
  'admin:invites',
  'admin:users',
  'admin:enrollment',
];

export interface OAuthClient {
  id: string;
  name: string;
  secretHash: string;
  salt: string;
  scopes: OAuthScope[];
  createdAt: string;
  active: boolean;
}

export type PublicOAuthClient = Omit<OAuthClient, 'secretHash' | 'salt'>;

export interface OAuthDelegation {
  id: string;
  clientId: string;
  userId: string;
  scopes: OAuthScope[];
  createdAt: string;
  expiresAt: string;
  revoked: boolean;
}

export interface OAuthClientCredentials {
  clientId: string;
  clientSecret: string;
}

export interface OAuthTokenResponse {
  accessToken: string;
  tokenType: 'Bearer';
  expiresIn: number;
  scopes: OAuthScope[];
}

export interface OAuthDelegationResponse {
  delegationToken: string;
  tokenType: 'Bearer';
  expiresIn: number;
  scopes: OAuthScope[];
  userId: string;
}

// ─── Invite / Registration Tokens ────────────────────────────────────────────

export interface InviteToken {
  id: string;
  token: string;
  createdBy: string;   // admin userId
  createdAt: string;    // ISO 8601
  usedBy?: string;      // userId who used it
  usedAt?: string;      // ISO 8601
  expiresAt?: string;   // ISO 8601 (optional expiry)
  active: boolean;
}

export type PublicInviteToken = InviteToken;

// ─── API Responses ───────────────────────────────────────────────────────────

export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  user: PublicUser;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
}
